import { createFileRoute, Link } from '@tanstack/react-router';
import { CircleAlert } from 'lucide-react';
import { Fragment } from 'react';

import { PhoebeAsksFirstSection } from '@apps/web/components/settings/phoebe_asks_first_section';
import { PhoebeAutomationSections } from '@apps/web/components/settings/phoebe_automation_sections';
import { PlaybookEditAccessCard } from '@apps/web/components/settings/playbook_edit_access_card';
import { Alert, AlertDescription } from '@apps/web/components/ui/alert';
import {
    SettingsPageSections,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

export const Route = createFileRoute('/_app/settings/phoebe')({
    component: PhoebePage,
});

function PhoebePage() {
    // No permission check: every endpoint this page calls admits any member of
    // the organization, so anyone who can reach it can already use it.
    const { organizationId, isLoading, gates } = useSettingsGates();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings',
    });
    const agentOutreachDisabled =
        organization?.callout_settings?.agent_outreach_enabled === false;
    if (isLoading) {
        return null;
    }
    if (organizationId === null) {
        return null;
    }

    return (
        <Fragment>
            {agentOutreachDisabled ? (
                <div className="mx-auto -mb-4 w-full max-w-4xl px-10 pt-12">
                    <Alert
                        variant="destructive"
                        className="border-destructive/40 bg-destructive/5"
                    >
                        <CircleAlert aria-hidden="true" />
                        <AlertDescription>
                            <p>
                                Agent-created outreach is off. Phoebe cannot
                                create outreaches until it is restored.
                            </p>
                            {gates.canManageOrgSettings ? (
                                <p>
                                    <Link
                                        to="/settings/outreach-automation"
                                        className="font-medium underline underline-offset-4"
                                    >
                                        Restore agent-created outreach in
                                        Scheduling settings
                                    </Link>
                                    .
                                </p>
                            ) : (
                                <p>
                                    Ask an account admin to restore
                                    agent-created outreach.
                                </p>
                            )}
                        </AlertDescription>
                    </Alert>
                </div>
            ) : null}
            <SettingsPageShell
                title="Phoebe"
                description="The work Phoebe takes on autonomously"
            >
                <SettingsPageSections>
                    <PlaybookEditAccessCard organizationId={organizationId} />
                    <PhoebeAutomationSections organizationId={organizationId} />
                    <PhoebeAsksFirstSection organizationId={organizationId} />
                </SettingsPageSections>
            </SettingsPageShell>
        </Fragment>
    );
}
